// src/pages/DataPrivacyPage.jsx
// Data & Privacy Settings: what this app keeps, where it goes, and the
// controls for it. The legal policies themselves are DDD-wide and live on
// divedeepdialogues.com (src/config/legalLinks.js), as in Relationship
// Compass. Every statement here was checked against the code in the
// September 2026 audit (docs/audits/authenticity-audit-art-of-story-2026-09.md).
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Shield,
  Lock,
  Cloud,
  Smartphone,
  CheckCircle,
  AlertCircle,
  Download,
  Trash2,
  Eye,
  EyeOff,
  HardDrive,
  Settings,
  Feather,
  ChevronLeft,
  ExternalLink
} from 'lucide-react';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { deleteCloudBackup } from '../services/cloudBackup';
import { DDD_PRIVACY_URL, DDD_TERMS_URL, DDD_COOKIES_URL } from '../config/legalLinks';

const POLICY_LINKS = [
  { label: 'Privacy Policy', href: DDD_PRIVACY_URL },
  { label: 'Terms of Use', href: DDD_TERMS_URL },
  { label: 'Cookie Policy', href: DDD_COOKIES_URL }
];

// .btn-primary (index.css) is unlayered and loads after Tailwind's utilities,
// so a plain bg-red-700 loses to its indigo gradient; hence the ! modifiers.
const DANGER_BUTTON =
  '!bg-none !bg-red-700 hover:!bg-red-800 inline-flex items-center justify-center w-full sm:w-auto';

// Everything this app keeps on the device is in localStorage under the
// artOfStory_ prefix, so read the real figures rather than showing
// placeholders.
const APP_KEY_PREFIX = 'artOfStory_';

const readLocalData = () => {
  const data = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(APP_KEY_PREFIX)) data[key] = localStorage.getItem(key);
    }
  } catch (error) {
    console.error('Error reading local data:', error);
  }
  return data;
};

const computeStorageStats = () => {
  const data = readLocalData();
  const bytes = Object.entries(data).reduce(
    (sum, [key, value]) => sum + (key.length + (value ? value.length : 0)) * 2,
    0
  );
  const totalSize = bytes === 0
    ? 'None'
    : bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return { totalSize, itemCount: Object.keys(data).length };
};

const DataPrivacyPage = () => {
  const { user, auth, usesCloud, updateStoragePreference } = useAuth();
  const isSignedIn = !!user;
  const cloudBackup = isSignedIn && usesCloud;

  const [activeTab, setActiveTab] = useState('overview');
  const [storageStats, setStorageStats] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setStorageStats(computeStorageStats());
  }, []);

  const showStatus = (type, message, ms = 3000) => {
    setOperationStatus({ type, message });
    setTimeout(() => setOperationStatus(null), ms);
  };

  // Cloud backup is AuthContext's storage preference: on saves to this
  // device and your account ('both', the default); off keeps it on this
  // device only ('local').
  const handleCloudToggle = () => {
    if (!isSignedIn) return;
    const next = !cloudBackup;
    updateStoragePreference(next ? 'both' : 'local');
    showStatus('success', next
      ? 'Cloud backup is on: your progress also saves to your account.'
      : 'Cloud backup is off: new progress saves on this device only.');
  };

  const handleExportData = () => {
    try {
      setLoading(true);
      const data = {
        exportedAt: new Date().toISOString(),
        source: 'The Art of Story (data stored on this device)',
        localStorage: readLocalData()
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `art-of-story-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showStatus('success', 'Data exported.');
    } catch {
      showStatus('error', 'Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Deletes what cloud backup keeps in this app's Firestore (see
  // services/cloudBackup.js). It runs as the Firebase user, not the app's
  // local "signed in" flag, which can outlive the real session.
  const handleDeleteCloudBackup = async () => {
    if (!isSignedIn) return;
    const firebaseUser = auth?.currentUser;
    if (!firebaseUser) {
      showStatus('error', 'Please sign in again to delete your cloud backup.', 6000);
      return;
    }

    const confirmed = window.confirm(
      'Delete everything The Art of Story has saved to your account (your progress, Storytelling Style Profile result and tool entries)? Data on this device stays, and cloud backup will be switched off. This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      await deleteCloudBackup(db, firebaseUser.uid);
      // Switch backup off so the next save doesn't put it straight back.
      updateStoragePreference('local');
      showStatus('success', 'Your cloud backup is deleted, and cloud backup is off. Turn it on in Settings to save to your account again.', 6000);
    } catch (error) {
      console.error('Error deleting cloud backup:', error);
      showStatus('error', "Your cloud backup couldn't be deleted. Please try again, or email help@divedeepdialogues.com.", 6000);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllData = () => {
    const confirmed = window.confirm(
      'Delete all The Art of Story data stored on this device? This cannot be undone.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      Object.keys(readLocalData()).forEach((key) => localStorage.removeItem(key));
      try { sessionStorage.clear(); } catch { /* private mode */ }

      setOperationStatus({
        type: 'success',
        message: 'Data on this device cleared. Restarting the app…'
      });
      setStorageStats(computeStorageStats());
      // Reload so nothing from before the clear carries on in memory.
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      showStatus('error', 'Failed to clear data.');
    } finally {
      setLoading(false);
    }
  };

  const storageLocation = !isSignedIn
    ? 'Nothing saved yet'
    : cloudBackup ? 'This device + your account' : 'This device';

  const OverviewTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-amber-400" />
          Your Data Storage
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-300">Storage location:</span>
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-green-400" />
                <span className="text-green-400 text-right">{storageLocation}</span>
              </div>
            </div>

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-300">In transit:</span>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-green-400" />
                <span className="text-green-400">Encrypted (HTTPS)</span>
              </div>
            </div>

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-300">Data on this device:</span>
              <span className="text-white">{storageStats?.totalSize || 'Calculating...'}</span>
            </div>

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-300">Items stored:</span>
              <span className="text-white">{storageStats?.itemCount || 0}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-300">Cloud backup:</span>
              <div className="flex items-center gap-2">
                {cloudBackup ? (
                  <>
                    <Cloud className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400">On</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400">{isSignedIn ? 'Off' : 'Sign in to use it'}</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-300">Marlow:</span>
              <span className="text-white text-right">Only if you talk to her</span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-gradient-to-r from-green-900/20 to-blue-900/20 border-green-500/30">
        <div className="flex items-start gap-4">
          <Shield className="w-8 h-8 text-green-400 mt-1 shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Where Your Data Goes</h3>
            <div className="space-y-2 text-slate-300">
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                If you aren&apos;t signed in, the app doesn&apos;t save your progress, your Storytelling
                Style Profile or your tool entries. They last until you reload or close the page.
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                You sign in with your Dive Deep Dialogues account at auth.divedeepdialogues.com, the
                same account every Dive Deep Dialogues app uses. Signed in, your progress is saved on
                this device and, with cloud backup on (it starts on), to your account in Google
                Firebase, whose database for this app is in the United States, so you can use it on
                other devices.
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                If you talk to Marlow, what she remembers is stored in Australia, and each message is
                sent to Anthropic&apos;s Claude to write the reply (details below).
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                We count visits in totals using Cloudflare Web Analytics (no cookies, and it
                doesn&apos;t identify you). No third-party ads, and we never sell your data.
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                Data on this device isn&apos;t encrypted by the app, so anyone who can use this browser
                can see it. You can export or delete it any time on the Data Management tab.
              </p>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-start gap-4">
          <Feather className="w-8 h-8 text-amber-400 mt-1 shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">
              Marlow, the AI companion: only if you use her
            </h3>
            <div className="space-y-3 text-slate-300 text-sm leading-relaxed">
              <p>
                Nothing is sent anywhere until you open Marlow, agree to her consent screen and
                send a message. Reading the modules, taking the assessment and using the toolkit
                never involve her.
              </p>
              <p>
                <span className="font-semibold text-white">What is sent when you do:</span> your
                message, the recent conversation, the module&apos;s name (and the section you were on)
                if you opened her from a module, a one-line summary of your Storytelling Style
                Profile (its name, with your strongest and weakest dimension), and what Marlow already
                keeps about you (what you told her about yourself and notes from earlier
                conversations). Your individual answers, saved progress and tool entries are not
                sent. It goes to the DDD Knowledge Hub, our own service, which uses Anthropic&apos;s
                Claude to write each reply.
              </p>
              <p>
                <span className="font-semibold text-white">What is kept:</span> what Marlow
                remembers is stored in Australia: what you&apos;ve told her about yourself, full
                transcripts of your conversations with her and summaries of them. To write each
                reply, your message is sent to Anthropic&apos;s Claude, which doesn&apos;t use it to
                train AI. We never sell your data or share it with advertisers. You can read, export
                or delete everything she keeps from the &ldquo;What Marlow keeps&rdquo; panel on her
                own page. Clearing data on this page doesn&apos;t touch what Marlow keeps.
              </p>
              <p className="text-slate-400">
                Marlow is not a therapist, counsellor or doctor, and storytelling is not therapy.
                If something with real personal weight arrives she will say so and point you to
                human support rather than carrying on with the craft.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const SettingsTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Cloud className="w-5 h-5 text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Cloud Backup</h3>
          {!isSignedIn && (
            <div className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
              <Lock className="w-3 h-3" />
              Sign In Required
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-lg">
          <div>
            <h4 className="text-white font-medium">Save to your account</h4>
            <p className="text-slate-400 text-sm">
              {isSignedIn
                ? 'On: your progress, profile result and tool entries also save to your account, so they sync across your devices. Off: new progress stays on this device only.'
                : 'Sign in to save your progress. Without an account, nothing is saved between visits.'}
            </p>
          </div>
          <button
            onClick={handleCloudToggle}
            disabled={!isSignedIn}
            role="switch"
            aria-checked={cloudBackup}
            aria-label="Save to your account"
            className={`w-12 h-6 shrink-0 rounded-full transition-colors relative ${
              cloudBackup ? 'bg-blue-500' : 'bg-slate-600'
            } ${!isSignedIn ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <motion.div
              className="w-5 h-5 rounded-full bg-white absolute top-0.5"
              animate={{ left: cloudBackup ? '1.75rem' : '0.25rem' }}
            />
          </button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5 text-green-400" />
          Visit counts
        </h3>
        <p className="text-slate-300 text-sm">
          We count visits in totals using Cloudflare Web Analytics (no cookies, and it doesn&apos;t
          identify you). No third-party ads, and we never sell your data.
        </p>
      </Card>
    </div>
  );

  const ManagementTab = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-400" />
          Export Your Data
        </h3>

        <p className="text-slate-300 mb-4">
          Download everything The Art of Story has saved on this device, in JSON format: your
          progress, Storytelling Style Profile result, tool entries and settings. Marlow&apos;s
          conversations have their own export on her page.
        </p>

        <Button
          onClick={handleExportData}
          disabled={loading}
          className="inline-flex items-center justify-center w-full sm:w-auto"
        >
          <Download className="w-4 h-4 mr-2" />
          {loading ? 'Exporting...' : 'Export All Data'}
        </Button>
      </Card>

      <Card className="p-6 bg-red-900/20 border-red-500/30">
        <h3 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
          <Cloud className="w-5 h-5" />
          Delete Your Cloud Backup
        </h3>

        <p className="text-slate-300 mb-4">
          {isSignedIn ? (
            <>
              Permanently delete everything The Art of Story has saved to your account in Google
              Firebase: your progress, Storytelling Style Profile result, tool entries and completion
              stats. <strong className="text-red-300">This cannot be undone.</strong> Data on this
              device stays (Clear All Data below removes that), and cloud backup is switched off so
              nothing is saved straight back. Your account itself, and what Marlow keeps, aren&apos;t
              affected.
            </>
          ) : (
            'Sign in to delete what is saved to your account. Without an account, nothing is saved to the cloud.'
          )}
        </p>

        <Button
          onClick={handleDeleteCloudBackup}
          disabled={loading || !isSignedIn}
          className={DANGER_BUTTON}
        >
          <Cloud className="w-4 h-4 mr-2" />
          {loading ? 'Deleting...' : 'Delete Cloud Backup'}
        </Button>
      </Card>

      <Card className="p-6 bg-red-900/20 border-red-500/30">
        <h3 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          Clear All Data
        </h3>

        <p className="text-slate-300 mb-4">
          Permanently delete everything The Art of Story has saved on this device.
          <strong className="text-red-300"> This cannot be undone.</strong> Your cloud backup has its
          own button above. You can ask to delete your Dive Deep Dialogues account itself from
          your{' '}
          <a href="https://auth.divedeepdialogues.com/account/delete" className="text-amber-300 hover:text-amber-200 underline">
            account page
          </a>
          . To delete what Marlow keeps, use the &ldquo;What Marlow keeps&rdquo; panel on her page.
        </p>

        <Button
          onClick={handleClearAllData}
          disabled={loading}
          className={DANGER_BUTTON}
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {loading ? 'Clearing...' : 'Clear All Data'}
        </Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-charcoal via-gunmetal to-charcoal pt-32">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 py-2 text-amber-300 hover:text-white mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Data &amp; Privacy Settings
            </h1>
            <p className="text-lg text-slate-300">
              What the app stores, where it goes, and how to export or delete it.
            </p>
            <p className="text-sm text-slate-400 mt-3">
              Our privacy policy, terms and cookie policy are shared across all Dive Deep Dialogues apps:
            </p>
            <ul className="mt-2 flex flex-wrap justify-center gap-x-5 gap-y-1 text-sm">
              {POLICY_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 py-1 text-amber-300 hover:text-white underline"
                  >
                    {link.label}
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center mb-8">
            <div className="bg-slate-800/50 rounded-lg p-1 flex flex-wrap justify-center gap-1">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'management', label: 'Data Management', icon: HardDrive }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-md font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-amber-700 text-white'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <OverviewTab />
              </motion.div>
            )}

            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <SettingsTab />
              </motion.div>
            )}

            {activeTab === 'management' && (
              <motion.div
                key="management"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <ManagementTab />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Portalled: <main> is `relative z-10`, which would keep this under the
              Quick Guide and back-to-top buttons however high its own z-index. */}
          {operationStatus && createPortal(
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              role="status"
              className={`fixed bottom-4 right-4 left-4 sm:left-auto z-50 p-4 rounded-lg shadow-lg sm:max-w-sm ${
                operationStatus.type === 'success'
                  ? 'bg-green-900/90 text-green-300 border border-green-500/30'
                  : 'bg-red-900/90 text-red-300 border border-red-500/30'
              }`}
            >
              <div className="flex items-center gap-2">
                {operationStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 shrink-0" />
                )}
                <span>{operationStatus.message}</span>
              </div>
            </motion.div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
};

export default DataPrivacyPage;
